import {
    Chip,
    Divider,
    Link,
    ListItemIcon,
    Menu,
    MenuItem,
    Modal,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Badge from '@mui/material/Badge'
import AccountCircle from '@mui/icons-material/AccountCircle'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import ChatIcon from '@mui/icons-material/Chat'
import { useTranslation } from 'react-i18next'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IUser } from '../interface/IUser'
import DarkMode from '@mui/icons-material/DarkMode'
import Language from '@mui/icons-material/Language'
import LightMode from '@mui/icons-material/LightMode'
import Logout from '@mui/icons-material/Logout'
import { setTheme } from '../source/slice/ThemeSlice'
import { useMsal } from '@azure/msal-react'
import logo from '../img/logo.svg'
import { WorkdayIcon } from '../img/workday'
import mbtiLogo from '../img/mbti-logo.svg'
import MailIcon from '@mui/icons-material/Mail'
import GitHubIcon from '@mui/icons-material/GitHub'
import LaunchIcon from '@mui/icons-material/Launch'
import Report from './Reports/Report'

function Navigation() {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const user: IUser = useSelector((state: any) => state.user?.value)
    const theme = useSelector((state: any) => state.theme?.value)
    const { instance } = useMsal()

    const [supportModalOpen, setSupportModalOpen] = useState<boolean>(false)
    const handleModalClick = () => {
        setSupportModalOpen(true)
    }
    const handleModalClose = () => {
        setSupportModalOpen(false)
    }

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng)
    }

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const navMenuOpen = Boolean(anchorEl)
    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const changeTheme = (color: string) => {
        dispatch(setTheme(color))
    }

    const logout = async () => {
        await instance.logoutRedirect()
        await instance.logoutPopup()
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar
                position="static"
                sx={(theme) => ({
                    backgroundColor: theme.palette.common.black,
                    backgroundImage: 'none',
                    boxShadow: 'none',
                })}
            >
                <Toolbar style={{ gap: 10 }}>
                    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <img src={mbtiLogo} alt="MBTI-Logo" style={{ height: 50 }} />
                    </Box>
                    <img src={logo} alt="Logo" style={{ width: 32, height: 32 }} />

                    <Typography variant="h5" component="div" sx={{ fontFamily: 'MB Corpo A Title', flexGrow: 1 }}>
                        Time Tracking
                    </Typography>
                    <Chip label="Confidential" color="error" variant="outlined" />
                    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <Tooltip title={t('navigation.github')}>
                            <Link href="https://mercedes-benz.ghe.com/homeoftech/timetracking" underline="none" target="_blank" rel="noopener" color="inherit">
                                <IconButton size="large" color="inherit">
                                    <Badge>
                                        <GitHubIcon />
                                    </Badge>
                                </IconButton>
                            </Link>
                        </Tooltip>
                        <Tooltip title={t('navigation.reports')}>
                            <Badge>
                                <Report />
                            </Badge>
                        </Tooltip>
                        <Tooltip title={t('navigation.workday')}>
                            <Link href="https://wd3.myworkday.com/mercedesbenztechinnovation/d/home.htmld" underline="none" target="_blank" rel="noopener" color="inherit">
                                <IconButton size="large" color="inherit">
                                    <Badge>
                                        <WorkdayIcon />
                                    </Badge>
                                </IconButton>
                            </Link>
                        </Tooltip>

                        <Tooltip title={t('navigation.helpcenter')}>
                            <Link href="https://corpdir.sharepoint.com/sites/mbti-apps-and-tools/SitePages/Navision.aspx#hilfeartikel" underline="none" target="_blank" rel="noopener" color="inherit">
                                <IconButton size="large" color="inherit">
                                    <Badge>
                                        <QuestionMarkIcon />
                                    </Badge>
                                </IconButton>
                            </Link>
                        </Tooltip>

                        <Tooltip title={t('navigation.support.title')}>
                            <IconButton size="large" color="inherit" onClick={handleModalClick}>
                                <Badge>
                                    <ChatIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Modal open={supportModalOpen} onClose={handleModalClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 'calc(100% - 32px)',
                                    maxWidth: 800,
                                    height: 'calc(100% - 32px)',
                                    maxHeight: 'fit-content',
                                    overflow: 'auto',
                                    bgcolor: 'background.paper',
                                    border: 0,
                                    borderRadius: 1,
                                    boxShadow: 24,
                                    p: {
                                        xs: 2,
                                        sm: 4,
                                    },
                                }}
                            >
                                <Typography id="modal-modal-title" variant="h6" component="h2">
                                    {t('navigation.support.title')}
                                </Typography>
                                <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                    {t('navigation.support.description')}
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.hrHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack alignItems="center" direction="row" gap={2}>
                                        <MailIcon color="inherit" fontSize="small" />
                                        <Link href="mailto:mbti-hr-services@mercedes-benz.com" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.support.hrLink')}
                                        </Link>
                                    </Stack>
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.pfcHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack alignItems="center" direction="row" gap={2}>
                                        <MailIcon color="inherit" fontSize="small" />
                                        <Link href="mailto:mbti-pfc@mercedes-benz.com" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.support.pfcLink')}
                                        </Link>
                                    </Stack>
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.controllingHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack alignItems="center" direction="row" gap={2}>
                                        <MailIcon color="inherit" fontSize="small" />
                                        <Link href="mailto:mbti-controlling@mercedes-benz.com" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.support.controllingLink')}
                                        </Link>
                                    </Stack>
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.serviceportalHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack alignItems="center" direction="row" gap={2}>
                                        <LaunchIcon color="inherit" fontSize="small" />
                                        <Link href="https://mbti-serviceportal.app.corpintra.net/wm/app-SelfServicePortalTSS/landing-page/0da8c041-fbc7-ea11-4e91-005056b1ba68?view-options=%7B%22dialogId%22:%222db6f3ef-9b9b-eb11-9181-005056b1b743%22,%22type%22:%22SVMChangeRequestType%22,%22viewType%22:%22new%22%7D&presetParams=%7B%22SPSActivityClassBase%22:%7B%22CUSTSSPType%22:%22110%22,%20%22CUST_FOTicketType%22:%20%2210%22%7D%7D" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.support.serviceportalLink')}
                                        </Link>
                                    </Stack>
                                    <Stack alignItems="center" direction="row" gap={2}>
                                        <Typography>{t('navigation.support.serviceportalInfo')}</Typography>
                                        <MailIcon color="inherit" fontSize="small" />
                                        <Link href="mailto:mbti-tickets.pool-id@mercedes-benz.com?subject=Request FO Timetracking - <describe your problem>&cc=mbti-navteam@mercedes-benz.com" target="_blank" rel="noopener" underline="hover">
                                           Support Mail
                                        </Link>
                                        
                                    </Stack>
                                </Typography>
                                <Typography sx={{ mt: 4 }}>{t('navigation.support.githubHeader')}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <Stack alignItems="center" direction="row" gap={2}>
                                        <GitHubIcon color="inherit" fontSize="small" />
                                        <Link href="https://mercedes-benz.ghe.com/homeoftech/timetracking" target="_blank" rel="noopener" underline="hover">
                                            {t('navigation.github')}
                                        </Link>
                                    </Stack>
                                </Typography>
                            </Box>
                        </Modal>
                        <IconButton size="large" edge="end" aria-haspopup="true" color="inherit" aria-controls={navMenuOpen ? 'basic-menu' : undefined} aria-expanded={navMenuOpen ? 'true' : undefined} onClick={handleMenuClick}>
                            <AccountCircle />
                        </IconButton>
                        <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={navMenuOpen}
                            onClose={handleMenuClose}
                            slotProps={{ list: {'aria-labelledby': 'basic-button' }}}
                        >
                            <Stack direction="row" alignItems="center" gap={1} px={2} paddingTop={1}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {user?.worker?.mserp_name}
                                </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" gap={1} px={2}>
                                <Typography variant="body2" sx={{ color: 'grey' }}>
                                    {user?.resource?.mserp_resourceid}
                                </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" gap={1} px={2} paddingBottom={1}>
                                <Typography variant="body2" sx={{ color: 'grey' }}>
                                    {user.userid}
                                </Typography>
                            </Stack>
                            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
                            <Stack alignItems="center" px={2} py={1}>
                                <ToggleButtonGroup exclusive onChange={(e, val: string) => changeLanguage(val)} aria-label="text alignment">
                                    <ToggleButton value="de" aria-label="left aligned" selected={i18n.language === 'de'} style={{ width: '108px', gap: '6px' }}>
                                        <Language /> Deutsch
                                    </ToggleButton>
                                    <ToggleButton value="en" aria-label="right aligned" selected={i18n.language === 'en'} style={{ width: '108px', gap: '6px' }}>
                                        <Language /> English
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Stack alignItems="center" px={2} py={1}>
                                <ToggleButtonGroup exclusive onChange={(e, val: string) => changeTheme(val)} aria-label="text 2">
                                    <ToggleButton value="light" aria-label="right aligned" selected={theme === 'light'} style={{ width: '150px', gap: '6px' }}>
                                        <LightMode /> {t('navigation.lightMode')}
                                    </ToggleButton>
                                    <ToggleButton value="dark" aria-label="left aligned" selected={theme === 'dark'} style={{ width: '150px', gap: '6px' }}>
                                        <DarkMode /> {t('navigation.darkMode')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
                            <MenuItem component="a" href="https://mercedes-benz.ghe.com/homeoftech/timetracking/releases" target="_blank">
                                <ListItemIcon>
                                    <GitHubIcon fontSize="small" />
                                </ListItemIcon>
                                {t('navigation.releaseversion')}
                                {process.env.REACT_APP_VERSION}
                            </MenuItem>
                            <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
                            <MenuItem onClick={logout}>
                                <ListItemIcon>
                                    <Logout fontSize="small" />
                                </ListItemIcon>
                                {t('navigation.logout')}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
        </Box>
    )
}

export default Navigation
